import React from "react";
import ImageIcon from "@mui/icons-material/Image";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const Image = ({ component, imagePreview, onImageClick, isEditMode = false, value, onChange, readOnly = false }) => {
  // Determinar qué imagen mostrar basado en el modo
  const getImageSrc = () => {
    if (isEditMode) {
      // Modo edición: mostrar imagen del componente o preview
      return imagePreview || component.src;
    } else {
      // Modo respuesta: mostrar imagen de la respuesta o placeholder del componente
      return value || component.src;
    }
  };

  // Función para comprimir imagen
  const compressImage = (file, maxWidth = 800, maxHeight = 600, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new window.Image(); // Usar window.Image para evitar conflictos

      img.onload = () => {
        // Calcular nuevas dimensiones manteniendo aspecto
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        // Dibujar imagen redimensionada
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir a blob comprimido
        canvas.toBlob(resolve, "image/jpeg", quality);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Handler para modo respuesta con compresión
  const handleResponseImageClick = async () => {
    if (readOnly) return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          // Comprimir imagen antes de procesarla
          const compressedBlob = await compressImage(file, 800, 600, 0.8);
          const reader = new FileReader();
          reader.onload = (ev) => {
            onChange && onChange(ev.target.result);
          };
          reader.readAsDataURL(compressedBlob);
        } catch (error) {
          console.error("Error comprimiendo imagen:", error);
          // Fallback al archivo original si falla la compresión
          const reader = new FileReader();
          reader.onload = (ev) => {
            onChange && onChange(ev.target.result);
          };
          reader.readAsDataURL(file);
        }
      }
    };
    input.click();
  };

  // Determinar si es clickeable
  // Solo es clickeable en modo respuesta (NO en modo edición)
  const isClickable = !isEditMode && !readOnly;

  // Determinar función onClick
  const handleClick = () => {
    // En modo edición, no hacer nada al hacer click
    if (isEditMode) {
      return;
    }

    // Solo en modo respuesta y no readonly
    if (!isEditMode && !readOnly) {
      handleResponseImageClick();
    }
  };

  const imageSrc = getImageSrc();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        minHeight: 150,
        border: imageSrc ? "none" : "2px dashed #ccc",
        borderRadius: 8,
        backgroundColor: imageSrc ? "transparent" : "#f9f9f9",
        cursor: isClickable ? "pointer" : "default",
        ...component.style,
        position: "relative",
      }}
      onClick={isClickable ? handleClick : undefined}
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={component.alt || "Imagen del formulario"}
          style={{
            width: "100%",
            height: "100%",
            objectFit: component.objectFit || "contain",
            borderRadius: component.style?.borderRadius || 8,
            maxWidth: "100%",
            maxHeight: "100%",
          }}
        />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            color: "#666",
            textAlign: "center",
            padding: 20,
          }}
        >
          {isEditMode ? (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, color: "#ccc" }} />
              <span style={{ fontSize: 14, fontWeight: "medium" }}>Haz clic para cargar imagen</span>
              <span style={{ fontSize: 12, color: "#999" }}>Formatos soportados: JPG, PNG, GIF</span>
            </>
          ) : readOnly ? (
            <>
              <ImageIcon sx={{ fontSize: 48, color: "#ccc" }} />
              <span style={{ fontSize: 14 }}>{component.placeholder || "Sin imagen"}</span>
            </>
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, color: "#ccc" }} />
              <span style={{ fontSize: 14, fontWeight: "medium" }}>Haz clic para cargar imagen</span>
              <span style={{ fontSize: 12, color: "#999" }}>Adjunta una imagen como respuesta</span>
            </>
          )}
        </div>
      )}

      {/* Overlay para modo respuesta y modo edición (cuando hay imagen) */}
      {imageSrc && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0,
            transition: "opacity 0.2s",
            borderRadius: component.style?.borderRadius || 8,
          }}
          className="image-overlay"
        >
          <CloudUploadIcon sx={{ fontSize: 32, color: "white" }} />
        </div>
      )}

      <style jsx>{`
        .image-overlay:hover {
          opacity: 1 !important;
        }
      `}</style>
    </div>
  );
};

export default Image;
